import { Box, InputAdornment, TextField, Tooltip } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { SettingsContact, SettingsSample } from '../sampleData';
import type { DetailsPatch, DetailsValues } from '../state';

import { FIELD_HEIGHT_SX, FormField } from './FormField';
import { PersonPicker } from './PersonPicker';
import type { PersonOption } from './PersonPicker';

import { MaterialSymbol } from '@/components/MaterialSymbol';

/**
 * Who's involved, edited (brief §3): a person picker each for the project
 * manager, account manager and partner — always set — and the contact, from
 * the customer's contacts, which may be left empty. The customer is
 * read-only: set when the project was created.
 */
export type PeopleFormProps = {
  draft: Pick<DetailsValues, 'projectManagerId' | 'accountManagerId' | 'partnerId' | 'contactId'>;
  sample: SettingsSample;
  contacts: SettingsContact[];
  customerName: string | undefined;
  onChange: (patch: DetailsPatch) => void;
};

export const PeopleForm = ({
  draft,
  sample,
  contacts,
  customerName,
  onChange,
}: PeopleFormProps) => {
  const { t } = useTranslation('pmDetails');
  const departmentName = new Map(sample.departments.map((d) => [d.id, d.name]));
  const employees: PersonOption[] = sample.employees.map((employee) => ({
    id: employee.id,
    name: employee.name,
    sub: departmentName.get(employee.departmentId) ?? '',
  }));
  const contactOptions: PersonOption[] = contacts.map((contact) => ({
    id: contact.id,
    name: contact.name,
    sub: contact.title,
  }));
  const owners = [
    { key: 'projectManagerId', label: t('people.ProjectManager'), id: 'details-project-manager' },
    { key: 'accountManagerId', label: t('people.AccountManager'), id: 'details-account-manager' },
    { key: 'partnerId', label: t('people.Partner'), id: 'details-partner' },
  ] as const;

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
        gap: 2,
      }}
    >
      {owners.map((owner) => (
        <FormField key={owner.key} label={owner.label} htmlFor={owner.id}>
          <PersonPicker
            id={owner.id}
            options={employees}
            value={draft[owner.key]}
            onChange={(id) => {
              if (id) onChange({ [owner.key]: id });
            }}
          />
        </FormField>
      ))}
      <FormField label={t('people.Contact')} htmlFor="details-contact">
        <PersonPicker
          id="details-contact"
          options={contactOptions}
          value={draft.contactId}
          onChange={(contactId) => onChange({ contactId })}
          clearable
        />
      </FormField>
      <FormField label={t('people.Customer')} htmlFor="details-customer">
        <Tooltip title={t('people.CustomerLocked')}>
          <TextField
            id="details-customer"
            size="small"
            value={customerName ?? '—'}
            sx={[
              FIELD_HEIGHT_SX,
              {
                '& .MuiInputBase-root': {
                  bgcolor: 'var(--color-fog-10)',
                  color: 'var(--color-fog-80)',
                },
              },
            ]}
            slotProps={{
              input: {
                readOnly: true,
                endAdornment: (
                  <InputAdornment position="end">
                    <MaterialSymbol name="lock" size={18} color="var(--color-fog-80)" />
                  </InputAdornment>
                ),
              },
              htmlInput: { 'data-automation-id': 'details-customer', 'aria-readonly': true },
            }}
          />
        </Tooltip>
      </FormField>
    </Box>
  );
};
